<template>
  <div class="space-y-6">
    <!-- Analytics Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-white">Analytics Dashboard</h2>
        <p class="text-white/70 mt-1">Platform insights and statistics</p>
      </div>
      <button
        @click="refreshAnalytics"
        :disabled="loading"
        class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center space-x-2"
      >
        <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 7 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>{{ loading ? 'Loading...' : 'Refresh' }}</span>
      </button>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
      <div class="flex items-center space-x-2">
        <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-red-400 font-medium">Error loading analytics</span>
      </div>
      <p class="text-red-300 mt-1">{{ error }}</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !analytics" class="flex items-center justify-center py-12">
      <div class="flex items-center space-x-3">
        <svg class="animate-spin h-6 w-6 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 7 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-white">Loading analytics...</span>
      </div>
    </div>

    <!-- Analytics Content -->
    <div v-if="analytics && !loading" class="space-y-6">
      <!-- Overview Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-white/70 text-sm font-medium">Total Users</p>
              <p class="text-3xl font-bold text-white">{{ formatNumber(analytics.overview.totalUsers) }}</p>
            </div>
            <div class="p-3 bg-blue-500/20 rounded-lg">
              <svg class="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
          </div>
          <div class="mt-4">
            <span class="text-green-400 text-sm font-medium">{{ analytics.overview.activeUsers }}</span>
            <span class="text-white/70 text-sm"> active</span>
          </div>
        </div>

        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-white/70 text-sm font-medium">Total Subscribers</p>
              <p class="text-3xl font-bold text-white">{{ formatNumber(analytics.overview.totalSubscribers) }}</p>
            </div>
            <div class="p-3 bg-purple-500/20 rounded-lg">
              <svg class="h-6 w-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 4v10a1 1 0 001 1h8a1 1 0 001-1V8M7 8h10M9 12h6m-6 4h6" />
              </svg>
            </div>
          </div>
          <div class="mt-4">
            <span class="text-blue-400 text-sm font-medium">{{ formatNumber(analytics.overview.avgSubscribersPerChannel) }}</span>
            <span class="text-white/70 text-sm"> avg per channel</span>
          </div>
        </div>

        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-white/70 text-sm font-medium">Accepted Users</p>
              <p class="text-3xl font-bold text-white">{{ formatNumber(analytics.overview.acceptedUsers) }}</p>
            </div>
            <div class="p-3 bg-green-500/20 rounded-lg">
              <svg class="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div class="mt-4">
            <span class="text-yellow-400 text-sm font-medium">{{ analytics.overview.pendingUsers }}</span>
            <span class="text-white/70 text-sm"> pending</span>
          </div>
        </div>

        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-white/70 text-sm font-medium">Admin Reviews</p>
              <p class="text-3xl font-bold text-white">{{ formatNumber(analytics.overview.reviewedUsers) }}</p>
            </div>
            <div class="p-3 bg-orange-500/20 rounded-lg">
              <svg class="h-6 w-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
          <div class="mt-4">
            <span class="text-red-400 text-sm font-medium">{{ analytics.overview.rejectedUsers }}</span>
            <span class="text-white/70 text-sm"> rejected</span>
          </div>
        </div>
      </div>

      <!-- Growth Chart -->
      <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
        <h3 class="text-lg font-semibold text-white mb-4">Cumulative Growth (Last 12 Months)</h3>
        <div class="h-64">
          <GrowthChart :data="analytics.growth" />
        </div>
      </div>

      <!-- Demographics and Quality Metrics -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Top Channels -->
        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <h3 class="text-lg font-semibold text-white mb-4">Top Channels by Subscribers</h3>
          <div class="space-y-3">
            <div v-for="(channel, index) in analytics.topChannels.slice(0, 5)" :key="channel.userId" class="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div class="flex items-center space-x-3">
                <div class="flex items-center justify-center w-8 h-8 bg-blue-500/20 rounded-full">
                  <span class="text-blue-400 font-semibold text-sm">{{ index + 1 }}</span>
                </div>
                <div>
                  <p class="text-white font-medium">{{ channel.email }}</p>
                  <p class="text-white/70 text-sm">{{ formatDate(channel.createdAt) }}</p>
                </div>
              </div>
              <div class="text-right">
                <p class="text-white font-semibold">{{ formatNumber(channel.subscriberCount) }}</p>
                <p class="text-white/70 text-sm">subscribers</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Quality Metrics -->
        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <h3 class="text-lg font-semibold text-white mb-4">Average Quality Metrics</h3>
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <span class="text-white/70">Production Quality</span>
              <div class="flex items-center space-x-2">
                <div class="w-20 h-2 bg-white/20 rounded-full">
                  <div class="h-2 bg-blue-400 rounded-full" :style="{ width: `${(analytics.qualityMetrics.avgProductionQuality / 5) * 100}%` }"></div>
                </div>
                <span class="text-white font-medium w-8">{{ analytics.qualityMetrics.avgProductionQuality }}</span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-white/70">Audio Clarity</span>
              <div class="flex items-center space-x-2">
                <div class="w-20 h-2 bg-white/20 rounded-full">
                  <div class="h-2 bg-green-400 rounded-full" :style="{ width: `${(analytics.qualityMetrics.avgAudioClarity / 5) * 100}%` }"></div>
                </div>
                <span class="text-white font-medium w-8">{{ analytics.qualityMetrics.avgAudioClarity }}</span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-white/70">Instructional Value</span>
              <div class="flex items-center space-x-2">
                <div class="w-20 h-2 bg-white/20 rounded-full">
                  <div class="h-2 bg-purple-400 rounded-full" :style="{ width: `${(analytics.qualityMetrics.avgInstructionalValue / 5) * 100}%` }"></div>
                </div>
                <span class="text-white font-medium w-8">{{ analytics.qualityMetrics.avgInstructionalValue }}</span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-white/70">Visual Complexity</span>
              <div class="flex items-center space-x-2">
                <div class="w-20 h-2 bg-white/20 rounded-full">
                  <div class="h-2 bg-orange-400 rounded-full" :style="{ width: `${(analytics.qualityMetrics.avgVisualComplexity / 5) * 100}%` }"></div>
                </div>
                <span class="text-white font-medium w-8">{{ analytics.qualityMetrics.avgVisualComplexity }}</span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-white/70">Editing Sophistication</span>
              <div class="flex items-center space-x-2">
                <div class="w-20 h-2 bg-white/20 rounded-full">
                  <div class="h-2 bg-red-400 rounded-full" :style="{ width: `${(analytics.qualityMetrics.avgEditingSophistication / 5) * 100}%` }"></div>
                </div>
                <span class="text-white font-medium w-8">{{ analytics.qualityMetrics.avgEditingSophistication }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Demographics Charts -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Channel Categories -->
        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <h3 class="text-lg font-semibold text-white mb-4">Channel Categories</h3>
          <div class="space-y-2">
            <div v-for="(count, category) in analytics.demographics.channelCategories" :key="category" class="flex justify-between items-center">
              <span class="text-white/70 text-sm">{{ category }}</span>
              <div class="flex items-center space-x-2">
                <div class="w-16 h-2 bg-white/20 rounded-full">
                  <div class="h-2 bg-blue-400 rounded-full" :style="{ width: `${(count / Math.max(...Object.values(analytics.demographics.channelCategories))) * 100}%` }"></div>
                </div>
                <span class="text-white font-medium text-sm w-6">{{ count }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Languages -->
        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <h3 class="text-lg font-semibold text-white mb-4">Primary Languages</h3>
          <div class="space-y-2">
            <div v-for="(count, language) in analytics.demographics.languageStats" :key="language" class="flex justify-between items-center">
              <span class="text-white/70 text-sm">{{ language }}</span>
              <div class="flex items-center space-x-2">
                <div class="w-16 h-2 bg-white/20 rounded-full">
                  <div class="h-2 bg-green-400 rounded-full" :style="{ width: `${(count / Math.max(...Object.values(analytics.demographics.languageStats))) * 100}%` }"></div>
                </div>
                <span class="text-white font-medium text-sm w-6">{{ count }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Countries -->
        <div class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
          <h3 class="text-lg font-semibold text-white mb-4">Production Countries</h3>
          <div class="space-y-2">
            <div v-for="(count, country) in analytics.demographics.countryStats" :key="country" class="flex justify-between items-center">
              <span class="text-white/70 text-sm">{{ country }}</span>
              <div class="flex items-center space-x-2">
                <div class="w-16 h-2 bg-white/20 rounded-full">
                  <div class="h-2 bg-purple-400 rounded-full" :style="{ width: `${(count / Math.max(...Object.values(analytics.demographics.countryStats))) * 100}%` }"></div>
                </div>
                <span class="text-white font-medium text-sm w-6">{{ count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Last Updated -->
      <div class="text-center">
        <p class="text-white/50 text-sm">
          Last updated: {{ formatDate(analytics.generatedAt) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { apiCall } from '../config/api.js'
import GrowthChart from './GrowthChart.vue'

export default {
  name: 'AdminAnalytics',
  components: {
    GrowthChart
  },
  data() {
    return {
      analytics: null,
      loading: false,
      error: null
    }
  },
  async mounted() {
    await this.fetchAnalytics()
    

  },
  methods: {
    async fetchAnalytics() {
      this.loading = true
      this.error = null
      
      try {
        const { response, data } = await apiCall('users/admin/analytics', {
          method: 'GET',
        })

        if (response.ok && data.success) {
          this.analytics = data.analytics
          

        } else {
          throw new Error(data.message || 'Failed to fetch analytics')
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching analytics:', error)
        }
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    async refreshAnalytics() {
      await this.fetchAnalytics()
    },
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script> 